// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import translate from "@insite/client-framework/Translate";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { MappedLink } from "@insite/mobius/Menu";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { FC, useRef } from "react";
import styled, { css } from "styled-components";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { useCurrentPage, useLocation } from "@toolkit/Framework/Store/Data/Pages/PageHooks";
import { usePageLinkByNodeId, usePageLinkByPageType } from "@toolkit/Framework/Store/Links/LinksHooks";
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import Clickable from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@store/MobiusOverrides/Icon/Icon";
import getIconByPageType from "@store/Framework/Common/Utilities/getIconByPageType";
import LogOut from "@store/Library/Components/Toolkit/Icons/LogOut";
import { focusStyles } from "@toolkit/Themes/common/mixins/common";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import getColor from "@insite/mobius/utilities/getColor";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import Tooltip, { TooltipComponentProps, TooltipPresentationProps } from "@store/MobiusOverrides/Tooltip/Tooltip";
import AccountMessagesCount, {
    AccountMessagesCountPresentationProps,
} from "@store/Library/Components/Toolkit/AccountMessagesCount";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import constants from "@store/constants";

const isNotificationsPage = (pageType: string | undefined) => pageType === constants.pageType.myAccount;

export const smallCollapsableSideMenuProps: CollapsableSideMenuPresentationProps = {
    overridedStyles: {
        wrapper: null as any,
    },
    extendedStyles: {
        listItem: {
            css: css`
                &.sign-out {
                    padding-top: ${pxToRem(8)};
                    border-top: 1px solid ${getColor("common.borderLight")};
                    margin-top: ${pxToRem(6)};
                }
            `,
        },
        link: {
            css: css`
                min-height: ${pxToRem(40)};
            `,
        },
    },
};

const List = styled.ul`
    ${injectCss}
`;

const ListItem = styled.li`
    ${injectCss}
`;

export interface CollapsableSideMenuPresentationProps extends ExpandStyles<CollapsableSideMenuStyles> {}

export interface CollapsableSideMenuProps extends CollapsableSideMenuPresentationProps {
    isCollapsed?: boolean;
    pageType?: string;
    nodeId?: string;
    showRootPage?: boolean;
    showSignOut?: boolean;
    onClick?: React.EventHandler<React.MouseEvent>;
    excludedPageTypes?: string[];
    links?: (MappedLink | PageLinkModel)[];
}

export interface CollapsableSideMenuStyles {
    wrapper?: InjectableCss;
    skipNav?: SkipNavStyles;
    list?: InjectableCss;
    listItem?: InjectableCss;
    link?: ClickablePresentationProps;
    linkText?: TypographyPresentationProps;
    collapsedLink?: ClickablePresentationProps;
    linkIcon?: IconPresentationProps;
    collapsedLinkIcon?: IconPresentationProps;
    collapsedLinkTooltip?: TooltipPresentationProps;
    accountMessagesCount?: AccountMessagesCountPresentationProps;
    collapsedAccountMessagesCount?: AccountMessagesCountPresentationProps;
}

const sharedLinkProps: ClickablePresentationProps = {
    css: css`
        ${focusStyles()}
        display: flex;
        align-items: center;
        gap: ${pxToRem(6)};
        padding: ${pxToRem(4, 8)};
        color: ${getColor("text.light")};
        border-radius: ${pxToRem(4)};
        font-size: ${x => x.theme.design.typography.size.smaller};
        font-weight: ${x => x.theme.design.typography.weight.medium};
        line-height: ${x => x.theme.design.typography.lineHeight.small};
        transition: ${transitionHoverStyles("color", "background-color")};

        &.on {
            color: ${getColor("primary")};
            background-color: ${getColor("success.lighter")};
        }

        &:not(.on) {
            &:hover,
            &:active {
                color: ${getColor("primary")};
                background-color: ${getColor("common.accent")};
            }
        }
    `,
};

const sharedLinkIconProps: IconPresentationProps = {
    color: "inherit",
};

export const collapsableSideMenuStyles: CollapsableSideMenuStyles = {
    wrapper: {
        css: css`
            padding: ${pxToRem(4)};
        `,
    },
    list: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: ${pxToRem(2)};
        `,
    },
    link: mergeProps<ClickablePresentationProps>(sharedLinkProps, {
        css: css`
            width: 100%;
            min-height: ${pxToRem(44)};
        `,
    }),
    linkText: {
        css: css`
            flex-grow: 1;
        `,
    },
    collapsedLink: mergeProps<ClickablePresentationProps>(sharedLinkProps, {
        css: css`
            justify-content: center;
            width: ${pxToRem(48)};
            height: ${pxToRem(48)};
        `,
    }),
    linkIcon: mergeProps<IconPresentationProps>(sharedLinkIconProps, {
        size: 24,
    }),
    collapsedLinkIcon: mergeProps<IconPresentationProps>(sharedLinkIconProps, {
        size: 28,
    }),
    collapsedLinkTooltip: {
        direction: "right",
        offset: 4,
        mergeCss: true,
        cssOverrides: {
            tooltipWrapper: css`
                margin: 0;
            `,
            tooltipBody: css`
                padding: ${pxToRem(8, 16)};
            `,
        },
    },
    accountMessagesCount: {
        extendedStyles: {
            messagesCountText: {
                css: css`
                    position: relative;
                    flex-shrink: 0;
                `,
            },
        },
    },
    collapsedAccountMessagesCount: {
        extendedStyles: {
            messagesCountText: {
                css: css`
                    top: ${pxToRem(5)};
                    left: ${pxToRem(24)};
                `,
            },
        },
    },
};

const CollapsableSideMenu: FC<CollapsableSideMenuProps> = ({
    isCollapsed,
    showRootPage,
    showSignOut,
    overridedStyles,
    extendedStyles,
    onClick,
    excludedPageTypes = [],
    links: baseLinks,
    pageType = "NONE",
    nodeId,
}) => {
    const dispatch = useAppDispatch();
    const currentLocation = useLocation();
    const currentPage = useCurrentPage();
    const pageLinkByPageType = usePageLinkByPageType(pageType);
    const pageLinkByNodeId = usePageLinkByNodeId(nodeId || currentPage.nodeId);
    const rootPageLink = pageLinkByPageType || pageLinkByNodeId;
    const links = (baseLinks?.length ? baseLinks : rootPageLink?.children) as
        | (MappedLink & PageLinkModel)[]
        | undefined;
    const isSignedIn = useAppSelector(state => state.context.session.userName && !getCurrentUserIsGuest(state));
    const styles = useExpandProps(collapsableSideMenuStyles, extendedStyles, overridedStyles);
    const afterMenu = useRef<HTMLSpanElement>(null);

    if (!links?.length && !showSignOut) {
        return null;
    }

    const currentPageUrl = currentLocation.pathname.toLowerCase();
    const filteredLinks = links?.filter(x => !x.excludeFromNavigation && !excludedPageTypes.some(y => x.type === y));

    const linkStyles = isCollapsed ? styles.collapsedLink : styles.link;
    const linkIconStyles = isCollapsed ? styles.collapsedLinkIcon : styles.linkIcon;
    const accountMessagesCountStyles = isCollapsed ? styles.collapsedAccountMessagesCount : styles.accountMessagesCount;

    const handleTooltip = (triggerComponent: TooltipComponentProps["triggerComponent"], text: string) => {
        if (!isCollapsed) {
            return triggerComponent;
        }

        return (
            <Tooltip
                {...styles.collapsedLinkTooltip}
                displayOnHover
                text={text}
                triggerIsClickable
                triggerComponent={triggerComponent}
            />
        );
    };

    return (
        <StyledWrapper as="nav" {...styles.wrapper}>
            <SkipNav destination={afterMenu} extendedStyles={styles.skipNav} text={translate("Skip Navigation")} />
            <List {...styles.list} data-test-selector="sideMenuList">
                {showRootPage && rootPageLink && !rootPageLink.excludeFromNavigation && (
                    <ListItem {...styles.listItem}>
                        {handleTooltip(
                            <Clickable
                                {...linkStyles}
                                className={currentPageUrl === rootPageLink.url.toLowerCase() ? "on" : undefined}
                                href={rootPageLink.url}
                                onClick={onClick}
                            >
                                {getIconByPageType(rootPageLink.type) && (
                                    <Icon {...linkIconStyles} src={getIconByPageType(rootPageLink.type)} />
                                )}
                                {isCollapsed ? (
                                    <VisuallyHidden>{rootPageLink.title}</VisuallyHidden>
                                ) : (
                                    <Typography {...styles.linkText}>{rootPageLink.title}</Typography>
                                )}
                                {isNotificationsPage(rootPageLink.type) && (
                                    <AccountMessagesCount {...accountMessagesCountStyles} />
                                )}
                            </Clickable>,
                            rootPageLink.title,
                        )}
                    </ListItem>
                )}
                {filteredLinks?.map((link, index) => {
                    const isCurrentPage = Boolean(link.url) && currentPageUrl.startsWith(link.url.toLowerCase());
                    const iconSrc = getIconByPageType(link.type);

                    return (
                        <ListItem key={link.id || `${link.title}_${index}`} {...styles.listItem}>
                            {handleTooltip(
                                <Clickable
                                    {...linkStyles}
                                    className={isCurrentPage ? "on" : undefined}
                                    href={link.url}
                                    onClick={(event: React.MouseEvent) => {
                                        link.clickableProps?.onClick?.(event);
                                        onClick?.(event);
                                    }}
                                >
                                    {iconSrc && <Icon {...linkIconStyles} src={iconSrc} />}
                                    {isCollapsed ? (
                                        <VisuallyHidden>{link.title}</VisuallyHidden>
                                    ) : (
                                        <Typography {...styles.linkText}>{link.title}</Typography>
                                    )}
                                    {isNotificationsPage(link.type) && (
                                        <AccountMessagesCount {...accountMessagesCountStyles} />
                                    )}
                                </Clickable>,
                                link.title,
                            )}
                        </ListItem>
                    );
                })}
                {isSignedIn && showSignOut && (
                    <ListItem {...styles.listItem} className="sign-out">
                        {handleTooltip(
                            <Clickable
                                {...linkStyles}
                                onClick={(event: React.MouseEvent) => {
                                    dispatch(signOut());
                                    onClick?.(event);
                                }}
                            >
                                <Icon {...linkIconStyles} src={LogOut} />
                                {isCollapsed ? (
                                    <VisuallyHidden>{translate("Sign Out")}</VisuallyHidden>
                                ) : (
                                    translate("Sign Out")
                                )}
                            </Clickable>,
                            translate("Sign Out"),
                        )}
                    </ListItem>
                )}
            </List>
            <span ref={afterMenu} tabIndex={-1} />
        </StyledWrapper>
    );
};

export default CollapsableSideMenu;
